/**
 * Exportacao de uma listagem para arquivo.
 *
 * O arquivo sai em CSV com separador de ponto e virgula e marca de ordem de bytes.
 * Os dois detalhes existem pelo mesmo motivo: o Excel em portugues abre assim sem
 * pedir nada. Virgula como separador quebra o valor monetario brasileiro em duas
 * colunas, e sem a marca de ordem os acentos viram simbolos.
 *
 * A exportacao acontece no navegador, sobre os dados que a tela pediu ao servidor
 * com os filtros aplicados. Ela exporta o que a pessoa esta vendo, e o cabecalho do
 * arquivo diz quantos registros sairam, para que ninguem confunda a pagina com o
 * total.
 */

/** Uma coluna do arquivo: o titulo e como extrair o valor de cada linha. */
export interface ColunaExportada<T> {
  titulo: string;
  valor: (item: T) => string | number | null | undefined;
}

/** Escapa um campo para o formato CSV. */
function campo(valor: string | number | null | undefined): string {
  if (valor === null || valor === undefined) return '';
  const texto = String(valor);
  // Aspas, ponto e virgula e quebra de linha precisam do campo entre aspas, com as
  // aspas internas dobradas. Sem isso uma observacao com ponto e virgula desloca
  // todas as colunas seguintes.
  if (/["\n;]/.test(texto)) {
    return `"${texto.replace(/"/g, '""')}"`;
  }
  return texto;
}

/**
 * Monta o conteudo CSV a partir das linhas e das colunas.
 *
 * Separado do download para que o formato possa ser conferido por teste sem tocar
 * no navegador.
 */
export function montarCsv<T>(itens: T[], colunas: ColunaExportada<T>[]): string {
  const cabecalho = colunas.map((coluna) => campo(coluna.titulo)).join(';');
  const linhas = itens.map((item) =>
    colunas.map((coluna) => campo(coluna.valor(item))).join(';'),
  );
  return [cabecalho, ...linhas].join('\r\n');
}

/**
 * Entrega o arquivo ao navegador.
 *
 * O objeto de URL e liberado depois do clique: sem isso, cada exportacao deixa o
 * conteudo inteiro preso na memoria da aba ate ela ser fechada.
 */
export function baixarCsv<T>(
  nome: string,
  itens: T[],
  colunas: ColunaExportada<T>[],
): void {
  const conteudo = montarCsv(itens, colunas);
  const marcaDeOrdem = '﻿';
  const blob = new Blob([marcaDeOrdem + conteudo], {
    type: 'text/csv;charset=utf-8',
  });

  const url = URL.createObjectURL(blob);
  const ancora = document.createElement('a');
  ancora.href = url;
  ancora.download = `${nome}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.append(ancora);
  ancora.click();
  ancora.remove();
  URL.revokeObjectURL(url);
}

/** Valor monetario no formato que a planilha brasileira entende. */
export function moedaParaPlanilha(valor: { amount: number; exponent: number }): string {
  return (valor.amount / 10 ** valor.exponent)
    .toFixed(valor.exponent)
    .replace('.', ',');
}
